import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../../lib/api";
import { Mail, CheckCircle2, Loader2 } from "lucide-react";

export const NewsletterSignup = ({ id = "newsletter", compact = false }) => {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState("idle");
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setState("sending");
    try {
      const { data } = await api.post("/newsletter/subscribe", { email, website });
      setState(data.status === "active" ? "already" : "pending");
    } catch (e) {
      setErr(e?.response?.status === 422 ? "Adresse email invalide." : e?.response?.data?.detail || "Une erreur est survenue.");
      setState("idle");
    }
  };

  if (state === "pending" || state === "already") {
    return (
      <div data-testid={`${id}-success`} className="flex items-start gap-3 text-sm text-neutral-700">
        <CheckCircle2 className="h-5 w-5 text-[var(--red)] shrink-0 mt-0.5" />
        <span>
          {state === "already"
            ? "Vous êtes déjà inscrit(e) — rendez-vous lundi matin !"
            : "Presque fini : cliquez sur le lien reçu par email pour confirmer votre inscription."}
        </span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${id}-form`} className="w-full">
      <div className={`flex ${compact ? "flex-col sm:flex-row" : "flex-col sm:flex-row"} gap-2`}>
        <label className="relative flex-1">
          <span className="sr-only">Votre email</span>
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.fr"
            data-testid={`${id}-email-input`}
            className="w-full h-11 pl-9 pr-3 rounded-sm border border-[var(--line-strong)] bg-white text-sm focus:outline-none focus:border-black transition-colors"
          />
        </label>
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          className="hidden"
          aria-hidden
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid={`${id}-submit-btn`}
          className="h-11 px-5 rounded-sm bg-black text-white text-xs uppercase tracking-[0.25em] hover:bg-[var(--red)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {state === "sending" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Je m'inscris
        </button>
      </div>
      {err && <div data-testid={`${id}-error`} className="text-xs text-[var(--red)] mt-2">{err}</div>}
      <p className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
        Un email par semaine, le lundi. Confirmation par email, désinscription en 1 clic.{" "}
        <Link to="/mentions-legales" className="underline underline-offset-2 hover:text-[var(--red)]">Données personnelles</Link>
      </p>
    </form>
  );
};

export const NewsletterBand = ({ placement = "footer" }) => {
  const { pathname } = useLocation();
  if (placement === "footer" && (pathname === "/newsletter" || pathname === "/")) return null;
  return (
  <section
    data-testid={placement === "home" ? "newsletter-band-home" : "newsletter-band"}
    className={placement === "home"
      ? "border-b border-[var(--line)] bg-[var(--bg-cream)] border-l-4 border-l-[var(--red)]"
      : "border-b border-[var(--line)] bg-white"}
  >
    <div className="max-w-[1300px] mx-auto px-5 md:px-10 py-10 grid md:grid-cols-[1fr_1.1fr] gap-6 md:gap-12 items-center">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">Newsletter</div>
        <h2 className={`font-display mt-1 ${placement === "home" ? "text-[19px] md:text-2xl" : "text-2xl md:text-3xl"}`} data-testid="newsletter-band-title">Les sorties de la semaine, chaque lundi matin.</h2>
        <p className={`text-neutral-600 mt-2 ${placement === "home" ? "text-[13px]" : "text-sm"}`} data-testid="newsletter-band-intro">Films, séries et derniers articles de la rédaction — en 2 minutes de lecture.</p>
      </div>
      <NewsletterSignup id="newsletter-band" />
    </div>
  </section>
);
};
